import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq } from 'ember-truth-helpers';
import UiButton from 'garaje/components/ui-button';
import UiInputField from 'garaje/components/ui-input-field';
import type { IdScanningFaceMatch, IdScanningNameMatch } from 'garaje/models/screener-result';
import type IdNameReview from 'garaje/utils/id-name-review';

interface IdNameMismatchCardSignature {
  Args: {
    nameMatch: IdScanningNameMatch | undefined;
    faceMatch: IdScanningFaceMatch | undefined;
    registeredName: string;
    nameReview: IdNameReview;
  };
}

/**
 * The ID verification card: the editable "legal name on ID" sections when an ID
 * scan flags a name mismatch, and/or the photo-check message when the face check
 * held the visit (one approval, one card). Owns the transient edit-form buffers;
 * the durable review state (mode/status/name) lives on the passed-in
 * `@nameReview` so the approval tiles can read the payload and disable review
 * while editing.
 */
export default class IdNameMismatchCard extends Component<IdNameMismatchCardSignature> {
  // Buffered field values while the inline edit form is open. Committed to the
  // review state on Confirm, discarded on Cancel.
  @tracked firstNameInput = '';
  @tracked lastNameInput = '';

  get mode(): IdNameReview['mode'] {
    return this.args.nameReview.mode;
  }

  /** Photo-check copy keyed on the backend's reason; default is the true-mismatch case. */
  get faceMessage(): string {
    const reason = this.args.faceMatch?.reason;
    if (reason === 'no_face_detected') return 'No face could be detected in the photo taken at sign-in.';
    if (reason === 'timeout') return 'The photo check could not be completed in time.';
    return 'The photo taken at sign-in does not match the portrait on this visitor’s government ID.';
  }

  get isRemoved(): boolean {
    return this.args.nameReview.status === 'removed';
  }

  /** Confirm is blocked until both legal name fields have content. */
  get confirmEditDisabled(): boolean {
    return this.firstNameInput.trim().length === 0 || this.lastNameInput.trim().length === 0;
  }

  /** Legal first name to display: the reviewer's edit when present, else the report value. */
  get legalFirstName(): string {
    if (this.args.nameReview.status === 'updated') return this.args.nameReview.firstName;
    return this.args.nameMatch?.['veriff-first-name'] ?? '';
  }

  /** Legal last name to display: the reviewer's edit when present, else the report value. */
  get legalLastName(): string {
    if (this.args.nameReview.status === 'updated') return this.args.nameReview.lastName;
    return this.args.nameMatch?.['veriff-last-name'] ?? '';
  }

  startEdit = (): void => {
    this.firstNameInput = this.legalFirstName;
    this.lastNameInput = this.legalLastName;
    this.args.nameReview.mode = 'edit';
  };

  startDelete = (): void => {
    this.args.nameReview.mode = 'delete';
  };

  cancel = (): void => {
    this.args.nameReview.mode = 'default';
  };

  confirmEdit = (): void => {
    const { nameReview } = this.args;
    nameReview.firstName = this.firstNameInput.trim();
    nameReview.lastName = this.lastNameInput.trim();
    nameReview.status = 'updated';
    nameReview.mode = 'default';
  };

  confirmDelete = (): void => {
    const { nameReview } = this.args;
    nameReview.status = 'removed';
    nameReview.mode = 'default';
  };

  updateFirstName = (value: string): void => {
    this.firstNameInput = value;
  };

  updateLastName = (value: string): void => {
    this.lastNameInput = value;
  };

  <template>
    <li class="text-sm my-4" data-test-id-scanning-name-mismatch>
      {{#if @faceMatch}}
        <p class="mb-0" data-test-id-scanning-face-mismatch-message>
          {{this.faceMessage}}
        </p>
      {{/if}}

      {{#if @nameMatch}}
        <p class="mb-0 {{if @faceMatch 'mt-4'}}" data-test-id-scanning-name-mismatch-message>
          The legal name of this visitor’s profile does not match what’s on their government ID.
        </p>

        <div class="mt-4">
          <div class="text-xs uppercase text-carbon-50 font-medium tracking-wide">Registered name</div>
          <div class="mt-1" data-test-id-scanning-registered-name data-private>{{@registeredName}}</div>
        </div>

        <div class="mt-4">
          <div class="flex items-center justify-between">
            <span class="text-xs uppercase text-carbon-50 font-medium tracking-wide">Legal name on ID</span>
            {{#unless this.isRemoved}}
              <span class="flex items-center gap-x-3">
                <UiButton class="!p-0 leading-none" data-test-id-scanning-edit-name {{on "click" this.startEdit}}>
                  {{svgJar "pencil-edit-16" height="16" width="16" class="align-middle"}}
                </UiButton>
                <UiButton class="!p-0 leading-none" data-test-id-scanning-delete-name {{on "click" this.startDelete}}>
                  {{svgJar "trash-16" height="16" width="16" class="align-middle [&_path]:fill-current"}}
                </UiButton>
              </span>
            {{/unless}}
          </div>

          {{#if (eq this.mode "edit")}}
            <UiInputField class="mt-2" as |Field|>
              <Field.label>First name</Field.label>
              <Field.input
                data-test-id-scanning-first-name-input
                data-private
                @value={{this.firstNameInput}}
                @update={{this.updateFirstName}}
              />
            </UiInputField>
            <UiInputField class="mt-2" as |Field|>
              <Field.label>Last name</Field.label>
              <Field.input
                data-test-id-scanning-last-name-input
                data-private
                @value={{this.lastNameInput}}
                @update={{this.updateLastName}}
              />
            </UiInputField>
            <div class="flex gap-x-2 mt-3">
              <UiButton
                class="btn btn--small btn--brand"
                data-test-id-scanning-confirm-edit
                disabled={{this.confirmEditDisabled}}
                {{on "click" this.confirmEdit}}
              >
                Confirm
              </UiButton>
              <UiButton
                class="btn btn--small btn--outline"
                data-test-id-scanning-cancel-edit
                {{on "click" this.cancel}}
              >
                Cancel
              </UiButton>
            </div>
          {{else if (eq this.mode "delete")}}
            <div class="mt-1" data-test-id-scanning-legal-name data-private>
              <div>First name:
                <strong>{{this.legalFirstName}}</strong></div>
              <div>Last name:
                <strong>{{this.legalLastName}}</strong></div>
            </div>
            <p class="text-papaya-50 mt-3 mb-0" data-test-id-scanning-delete-warning>
              Removing the legal name prevents Envoy from using it to match this profile in future ID scans
            </p>
            <div class="flex gap-x-2 mt-3">
              <UiButton
                class="btn btn--small btn--brand"
                data-test-id-scanning-confirm-delete
                {{on "click" this.confirmDelete}}
              >
                Remove name
              </UiButton>
              <UiButton
                class="btn btn--small btn--outline"
                data-test-id-scanning-cancel-delete
                {{on "click" this.cancel}}
              >
                Cancel
              </UiButton>
            </div>
          {{else if this.isRemoved}}
            <div class="mt-1">
              <p class="italic text-carbon-50 mb-0" data-test-id-scanning-name-not-recorded>Not recorded</p>
              <p class="mt-2 mb-0" data-test-id-scanning-name-removed>Legal name will not be saved to profile.</p>
            </div>
          {{else}}
            <div class="mt-1" data-test-id-scanning-legal-name data-private>
              <div>First name:
                <strong>{{this.legalFirstName}}</strong></div>
              <div>Last name:
                <strong>{{this.legalLastName}}</strong></div>
            </div>
          {{/if}}
        </div>
      {{/if}}
    </li>
  </template>
}
